/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  Accordion,
  AccordionItem,
  TextArea,
  TextInput,
  FormGroup
} from 'carbon-components-react';
import cx from 'classnames';
import _ from 'lodash';
import { useEffect, useState, useCallback } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '../../../store';
import { ConversationalSearchSection } from '../../ia/components/conversational-search/ConversationalSearchSection';
import { useElasticDraft } from '../hooks/useElasticDraft';
import {
  setDraftTitleOptionValue,
  setDraftBodyOptionValue,
  setDraftUrlOptionValue,
  setDraftElasticFilterValue,
  setDraftElasticQueryBodyValue,
  setIsElasticConfigValid
} from '../store/reducer';
import {
  selectContentMapping,
  selectDraftTitleOptionValue,
  selectDraftBodyOptionValue,
  selectDraftUrlOptionValue,
  selectDraftElasticFilterValue,
  selectDraftElasticQueryBodyValue
} from '../store/selectors';
import { ConfigureContentMessages as messages } from './messages';

import './ConfigureContent.scss';

export interface ConfigureContentProps {
  tabDisplay?: boolean;
  readOnly: boolean;
}
export const ConfigureContent = ({
  tabDisplay = false,
  readOnly
}: ConfigureContentProps) => {
  const draft = useElasticDraft();
  const dispatch = useDispatch();
  const { formatMessage: t } = useIntl();
  const mappings = useAppSelector(selectContentMapping);
  const [filter, setFilter] = useState(
    JSON.stringify(useAppSelector(selectDraftElasticFilterValue))
  );
  const [invalidFilter, setInvalidFilter] = useState(false);
  const [invalidFilterText, setInvalidFilterText] = useState('');

  const queryBody = useAppSelector(selectDraftElasticQueryBodyValue) || '';

  const [invalidQueryBody, setInvalidQueryBody] = useState(false);
  const [invalidQueryBodyText, setInvalidQueryBodyText] = useState('');

  const title = useAppSelector(selectDraftTitleOptionValue);
  const [invalidTitle, setInvalidTitle] = useState(false);
  const [invalidTitleText, setInvalidTitleText] = useState('');

  const body = useAppSelector(selectDraftBodyOptionValue);
  const [invalidBody, setInvalidBody] = useState(false);
  const [invalidBodyText, setInvalidBodyText] = useState('');

  const url = useAppSelector(selectDraftUrlOptionValue);

  const mappingsDropdown = mappings.map(item => ({
    id: item,
    text: item
  }));

  const [selectedTitleIndex, setSelectedTitleIndex] = useState(
    mappingsDropdown.findIndex(item => item.id === 'title')
  );

  const [selectedBodyIndex, setSelectedBodyIndex] = useState(
    mappingsDropdown.findIndex(item => item.id === 'text')
  );

  const [selectedUrlIndex, setSelectedUrlIndex] = useState(
    mappingsDropdown.findIndex(item => item.id === 'metadata.url')
  );

  useEffect(() => {
    if (selectedTitleIndex >= 0) {
      dispatch(
        setDraftTitleOptionValue(mappingsDropdown[selectedTitleIndex].id)
      );
    }
    if (selectedBodyIndex >= 0) {
      dispatch(setDraftBodyOptionValue(mappingsDropdown[selectedBodyIndex].id));
    }
    if (selectedUrlIndex >= 0) {
      dispatch(setDraftUrlOptionValue(mappingsDropdown[selectedUrlIndex].id));
    }
  }, []);

  useEffect(() => {
    setSelectedTitleIndex(
      mappingsDropdown.findIndex(item => item.id === 'title')
    );
    setSelectedBodyIndex(
      mappingsDropdown.findIndex(item => item.id === 'text')
    );
    setSelectedUrlIndex(
      mappingsDropdown.findIndex(item => item.id === 'metadata.url')
    );
  }, [mappingsDropdown]);

  // Update the parent panel on the validity of the inputs for the elastic instance.
  useEffect(() => {
    if (invalidFilter || invalidQueryBody) {
      dispatch(setIsElasticConfigValid(false));
    } else {
      dispatch(setIsElasticConfigValid(true));
    }
  }, [dispatch, invalidFilter, invalidQueryBody]);

  const filterChange = useCallback(
    e => {
      const filter = e?.target?.value ?? '';
      setFilter(filter);
      if (filter) {
        let filterArray;
        // Store expects an array of objects for the filter so we need to convert back and forth from a string to an
        // array.
        try {
          // JSON.parse thinks the json is invalid if there are any new line characters in the string. So first remove all
          // the white space OUTSIDE the key/values and then try to transform the string to an array.
          filterArray = JSON.parse(filter.replace(/(\r\n|\n|\r)/gm, ''));
        } catch (e) {
          console.log(e);
          // If the filter is not valid json then show a validation error.
          setInvalidFilter(true);
          setInvalidFilterText(t(messages.invalidJsonArrayValue));
          return;
        }

        // If filterArray is not an array, or does not contain only objects within the array, then show a validation error.
        if (
          !Array.isArray(filterArray) ||
          filterArray.filter(item => typeof item !== 'object').length > 0
        ) {
          setInvalidFilter(true);
          setInvalidFilterText(t(messages.invalidJsonArrayValue));
        } else {
          // If the filter is a valid json array then clear any validation errors and save the array.
          setInvalidFilter(false);
          setInvalidFilterText('');
          dispatch(setDraftElasticFilterValue(filterArray));
        }
      } else {
        // If the filter is blank then clear any validation errors and save the blank input.
        setInvalidFilter(false);
        setInvalidFilterText('');
        dispatch(setDraftElasticFilterValue(filter));
      }
    },
    [dispatch, t]
  );

  const queryBodyChange = useCallback(e => {
    const queryBody = e?.target?.value ?? '';
    dispatch(setDraftElasticQueryBodyValue(queryBody));
    if (queryBody) {
      let queryBodyObject;
      // Store expects an object for the query body so we need to convert back and forth from a string to an object.
      try {
        // JSON.parse thinks the json is invalid if there are any new line characters in the string. So first remove all
        // the white space OUTSIDE the key/values and then try to transform the string to an object.
        queryBodyObject = JSON.parse(queryBody.replace(/(\r\n|\n|\r)/gm, ''));
      } catch (e) {
        console.log(e);
        // If the queryBody is not valid json then show a validation error.
        setInvalidQueryBody(true);
        setInvalidQueryBodyText(t(messages.invalidJsonObjectValue));
        return;
      }

      // If queryBodyObject is not an object (or is an array) then show a validation error.
      if (
        typeof queryBodyObject !== 'object' ||
        Array.isArray(queryBodyObject)
      ) {
        setInvalidQueryBody(true);
        setInvalidQueryBodyText(t(messages.invalidJsonObjectValue));
      } else {
        // If the query body is a valid json object then clear any validation errors and save the object.
        setInvalidQueryBody(false);
        setInvalidQueryBodyText('');
        dispatch(setDraftElasticQueryBodyValue(queryBodyObject));
      }
    } else {
      // If the query body is blank then clear any validation errors and save the blank input.
      setInvalidQueryBody(false);
      setInvalidQueryBodyText('');
      dispatch(setDraftElasticQueryBodyValue(queryBody));
    }
  }, []);

  const titleChange = e => {
    const title = e?.target?.value ?? '';
    if (title === null || _.isEmpty(title?.trim())) {
      setInvalidTitle(true);
      setInvalidTitleText(t(messages.invalidEmptyValueText));
    } else {
      setInvalidTitle(false);
      setInvalidTitleText('');
    }
    dispatch(setDraftTitleOptionValue(title));
  };

  const bodyChange = e => {
    const body = e?.target?.value ?? '';
    if (body === null || _.isEmpty(body?.trim())) {
      setInvalidBody(true);
      setInvalidBodyText(t(messages.invalidEmptyValueText));
    } else {
      setInvalidBody(false);
      setInvalidBodyText('');
    }
    dispatch(setDraftBodyOptionValue(body));
  };

  const urlChange = e => {
    const url = e?.target?.value ?? '';
    // Url is optional so no validation needed to check that it exists.
    dispatch(setDraftUrlOptionValue(url));
  };

  // Update the parent panel on the validity of the inputs for the elastic instance.
  useEffect(() => {
    if (invalidBody || invalidTitle || invalidFilter || invalidQueryBody) {
      dispatch(setIsElasticConfigValid(false));
    } else {
      dispatch(setIsElasticConfigValid(true));
    }
  }, [dispatch, invalidBody, invalidFilter, invalidQueryBody, invalidTitle]);

  return (
    <div
      className={cx('ConfigureContent', { ConfigureContent__tab: tabDisplay })}>
      <>
        <div
          className={cx('ConfigureContent__title', {
            'ConfigureContent__title-tab': tabDisplay
          })}>
          <FormattedMessage {...messages.title} />
        </div>
        <div
          className={cx('ConfigureContent__description', {
            'ConfigureContent__description-tab': tabDisplay
          })}>
          <FormattedMessage
            {...messages.description}
            values={{ name: 'Elasticsearch' }}
          />
        </div>
      </>
      <FormGroup legendText="" className="ConfigureContent__form">
        <TextInput
          autoComplete="off"
          id="ConfigureContent__form-title-input"
          invalid={invalidTitle}
          invalidText={invalidTitleText}
          placeholder={t(messages.fieldPlaceholder)}
          readOnly={readOnly}
          labelText={t(messages.fieldTitle)}
          onChange={titleChange}
          size="md"
          value={title}
          type="text"
        />
        <TextInput
          autoComplete="off"
          id="ConfigureContent__form-body-input"
          invalid={invalidBody}
          invalidText={invalidBodyText}
          placeholder={t(messages.fieldPlaceholder)}
          readOnly={readOnly}
          labelText={t(messages.fieldBody)}
          onChange={bodyChange}
          size="md"
          value={body}
          type="text"
        />
        <TextInput
          autoComplete="off"
          id="ConfigureContent__form-url-input"
          placeholder={t(messages.fieldPlaceholder)}
          readOnly={readOnly}
          labelText={t(messages.fieldUrl)}
          onChange={urlChange}
          size="md"
          value={url}
          type="text"
        />
      </FormGroup>
      {tabDisplay && (
        <>
          <Accordion className="ConfigureContent__accordion" align="start">
            <AccordionItem
              title={t(messages.dropdownAdvancedSettings, {
                name: 'Elasticsearch'
              })}
              className="ElasticSearchConfigFields__form-advanced-settings-accordion">
              <TextArea
                labelText={t(messages.fieldFilter)}
                rows={10}
                id="ElasticSearchConfigFields__form-filter-area"
                invalid={invalidFilter}
                invalidText={invalidFilterText}
                onChange={filterChange}
                value={filter}
              />
              <TextArea
                labelText={t(messages.fieldQueryBody)}
                rows={10}
                id="ElasticSearchConfigFields__form-query-body-area"
                invalid={invalidQueryBody}
                invalidText={invalidQueryBodyText}
                onChange={queryBodyChange}
                value={
                  typeof queryBody === 'object'
                    ? JSON.stringify(queryBody)
                    : queryBody.toString()
                }
              />
            </AccordionItem>
          </Accordion>
          <ConversationalSearchSection draft={draft} readOnly={readOnly} />
        </>
      )}
    </div>
  );
};
